<template>
  <AuthLayout :back-to="{ name: 'Login' }" back-label="Back to login">
    <form class="form-stack" @submit.prevent="attemptPasswordReset()">
      <div class="auth-form-header">
        <h1>Set your new password</h1>
        <p>Choose a strong password for your Givaroo account.</p>
      </div>

      <InputField
        v-model:value="form.password.value"
        :errors="form.password.errors"
        label-size="small"
        input-text-size="small"
        @blur="form.password.validate()"
        type="password"
        label="New password"
        placeholder="Enter new password..."
      />
      <InputField
        v-model:value="form.passwordConfirmation.value"
        :errors="form.passwordConfirmation.errors"
        label-size="small"
        input-text-size="small"
        @blur="form.passwordConfirmation.validate()"
        type="password"
        label="Confirm new password"
        placeholder="Confirm new password..."
      />

      <BaseButton block variant="primary" size="md" type="submit">Reset password</BaseButton>
    </form>

    <Modal
      headerText="This link has expired"
      subheaderText="Your invitation link is no longer valid. Click Resend link to receive a new setup email."
      :modalOpen="expiredModalOpen"
      :showFailureIcon="true"
      :showCancelButton="true"
      ctaText="Resend link"
      @closeModal="resendInviteLink"
      @cancelAction="closeExpiredModal"
    />

    <Modal
      headerText="Check your email"
      subheaderText="If your account is eligible, you should receive a new invitation email shortly with a link to set your password."
      :modalOpen="resendSuccessModalOpen"
      :showSuccessIcon="true"
      ctaText="OK"
      @closeModal="closeResendSuccessModal"
    />
  </AuthLayout>
</template>

<script>
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { PasswordResetForm, UserApi } from '@/services/users/'
import InputField from '@/components/inputs/InputField'
import AuthLayout from '@/components/auth/AuthLayout.vue'
import Modal from '@/components/Modal'

function getResetErrorMessage(error) {
  const data = error?.response?.data
  if (!data) return null

  const tokenError = data['non-field-error'] || data.non_field_errors
  if (Array.isArray(tokenError)) {
    return tokenError[0]
  }
  return tokenError
}

export default {
  name: 'ResetPassword',
  components: {
    InputField,
    AuthLayout,
    Modal,
  },
  setup() {
    const form = ref(new PasswordResetForm())
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const expiredModalOpen = ref(false)
    const resendSuccessModalOpen = ref(false)

    function handleResetSuccess(data) {
      store.dispatch('setUser', data)
      router.push({ name: 'CampaignList' })
    }

    function handleResetFailure(error) {
      const message = getResetErrorMessage(error)
      if (message && String(message).toLowerCase().includes('invalid or expired')) {
        expiredModalOpen.value = true
        return
      }
      alert(message || error?.message || 'Unable to reset password.')
    }

    function attemptPasswordReset() {
      const unwrappedForm = form.value
      unwrappedForm.validate()
      if (!unwrappedForm.isValid) return

      const { uid, token } = route.params

      UserApi.csc
        .resetPassword({
          uid,
          token,
          password: unwrappedForm.password.value,
        })
        .then(handleResetSuccess)
        .catch(handleResetFailure)
    }

    function closeExpiredModal() {
      expiredModalOpen.value = false
    }

    function closeResendSuccessModal() {
      resendSuccessModalOpen.value = false
    }

    async function resendInviteLink() {
      try {
        await UserApi.csc.resendInviteLink({ uid: route.params.uid })
        expiredModalOpen.value = false
        resendSuccessModalOpen.value = true
      } catch (error) {
        alert('Unable to resend link. Please try again or contact support.')
      }
    }

    return {
      form,
      attemptPasswordReset,
      expiredModalOpen,
      resendSuccessModalOpen,
      resendInviteLink,
      closeExpiredModal,
      closeResendSuccessModal,
    }
  },
}
</script>
